// v242.90.0 — FRESH PROVIDER SIGN-IN, for re-authenticating a destructive action.
//
// Account deletion used to send `confirm: true` and nothing else. For an
// SSO-only account the server accepted that boolean as the entire
// authentication, so an unlocked phone was one tap away from an irreversible
// deletion. API v243.104.0 now demands a FRESH provider token whose `sub`
// matches the one stored on the account; this module is how the app gets one.
//
// ★ IT IS NOT A LOGIN. Nothing here touches the stored session token — the
//   caller is already signed in and is proving it is still the same person.
//   The native path deliberately signs the provider session out first so the
//   user actually re-authenticates instead of being handed a cached identity.
import { Capacitor } from '@capacitor/core'
import { SocialLogin } from '@capgo/capacitor-social-login'

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || ''
const APPLE_WEB_CLIENT_ID = 'com.orellahealth.signin'

let _socialInited = false
async function ensureSocialInit() {
  if (_socialInited) return
  await SocialLogin.initialize({
    google: {
      webClientId: '643624439824-l6h1btsuoj3a5n0k28607bbrjr2c32ed.apps.googleusercontent.com',
      iOSClientId: '643624439824-gagojv418nvr2sefqe87a04m542bde4j.apps.googleusercontent.com',
      iOSServerClientId: '643624439824-l6h1btsuoj3a5n0k28607bbrjr2c32ed.apps.googleusercontent.com',
    },
    apple: {},
  })
  _socialInited = true
}

/**
 * Which providers can re-authenticate this account.
 * Mirrors the server rule in deletion_identity_check(): a linked account
 * ("email,google") has a real password and must use it, so it lists none.
 */
export function ssoProvidersFor(authProvider) {
  const p = (authProvider || '').toLowerCase()
  if (!p || p.includes('email')) return []
  return ['google', 'apple'].filter((x) => p.includes(x))
}

/**
 * PROD-943 — WHICH linked provider this re-authentication uses.
 *
 * ★★★ The list above is in a FIXED order, and the delete screen used to take its
 *     first entry. An account with Google AND Apple linked was therefore always
 *     sent to Google — even right after an Apple sign-in, with no way to pick
 *     Apple — so a Sign in with Apple patient could not delete the account
 *     (App Store 5.1.1(v)). The server (deletion_identity_check) accepts a fresh
 *     token from EITHER linked provider whose `sub` matches; only the client chose.
 *
 * Order of authority: the patient's explicit choice, then the only provider,
 * then the provider of the current session. More than one provider and neither
 * of those known → null: the patient must choose, we do not guess.
 */
export function reauthProviderFor(providers, { chosen = null, session = null } = {}) {
  const list = Array.isArray(providers) ? providers : []
  if (chosen && list.includes(chosen)) return chosen
  if (list.length === 1) return list[0]
  if (session && list.includes(session)) return session
  return null
}

/** The value a password (e-mail) sign-in stores as the session user's `signInProvider`. */
export const PASSWORD_SIGN_IN = 'email'

/**
 * PROD-943b — HOW this deletion is confirmed: `{ method, choices, fromSession }`.
 *   method:  'password' | 'apple' | 'google' | null (not decided yet)
 *   choices: the providers to offer as buttons ([] = no choice is shown)
 *
 * ★★★ Alex, 2026-10-13 (Q53): confirm the deletion THE WAY THE PATIENT SIGNED IN.
 *     An Apple session confirms with Apple, a Google session with Google, an e-mail
 *     session with the password — exactly that one, and no choice is shown.
 *
 * Order of authority:
 *   1. `server`: what the server asked for after a refused attempt (serverAskedMethods).
 *      It wins, so the patient is never stuck on a method the server will not take.
 *   2. `session`: the method that opened this session (user.signInProvider), when the
 *      account has it linked. With the account type unknown (the read failed) the
 *      session's method is used as it is: that sign-in proves it was linked.
 *   3. Otherwise the rule from before this build (PROD-943): an account with a password
 *      uses it; one SSO provider is used directly; two are offered as a choice.
 *
 * ★★ An account with e-mail AND an SSO provider linked, in an Apple/Google session,
 *    is sent down the SSO path. API e5c7cdb6 (deletion_identity_check) still demands
 *    the password for any account with 'email' in auth_provider and answers
 *    `wrong_password`; the screen then falls back to the password (rule 1). The API
 *    change that accepts the session's provider is a separate build.
 */
export function reauthMethodFor(authProvider, { session = null, chosen = null, server = null } = {}) {
  const known = typeof authProvider === 'string'
  const p = known ? authProvider.toLowerCase() : ''
  if (Array.isArray(server) && server.length) {
    if (server.includes('password')) return { method: 'password', choices: [], fromSession: false }
    const sso = ['google', 'apple'].filter((x) => server.includes(x))
    return { method: reauthProviderFor(sso, { chosen }), choices: sso.length > 1 ? sso : [], fromSession: false }
  }
  const sess = session === PASSWORD_SIGN_IN ? 'password' : (session === 'apple' || session === 'google' ? session : null)
  if (sess) {
    const linked = !known || (sess === 'password' ? (!p || p.includes('email')) : p.includes(sess))
    if (linked) return { method: sess, choices: [], fromSession: true }
  }
  if (!known) return { method: null, choices: [], fromSession: false }
  const sso = ssoProvidersFor(p)
  if (!sso.length) return { method: 'password', choices: [], fromSession: false }
  return { method: reauthProviderFor(sso, { chosen }), choices: sso.length > 1 ? sso : [], fromSession: false }
}

/**
 * PROD-943b — the methods a refused deletion asks for, from the server's `providers`
 * list: 'google' / 'apple', and 'password' for 'email' or 'password'. [] = nothing usable.
 */
export function serverAskedMethods(providers) {
  if (!Array.isArray(providers)) return []
  const list = providers.map((x) => String(x || '').toLowerCase())
  const out = ['google', 'apple'].filter((x) => list.includes(x))
  if (list.includes('email') || list.includes('password')) out.unshift('password')
  return out
}

/**
 * PROD-943b (r_943 N2) — a provider sheet the patient DISMISSED. Each SDK says it its
 * own way, and the raw text used to reach the screen as an error:
 *   - native Apple (ASAuthorizationError.canceled, 1001) through the capgo plugin;
 *   - web Apple JS: { error: 'popup_closed_by_user' } (or 'user_cancelled_authorize');
 *   - a plugin or SDK message saying the user canceled / cancelled.
 */
export function isProviderCancel(err) {
  if (!err) return false
  const code = String(err.error ?? err.code ?? '')
  if (code === 'popup_closed_by_user' || code === 'user_cancelled_authorize' || code === '1001') return true
  const msg = String(err.message || err.errorMessage || (typeof err === 'string' ? err : ''))
  return /AuthorizationError error 1001|\berror 1001\b|\bcancell?ed\b/i.test(msg)
}

/** The display name of a provider (a brand name, the same in every language). */
export function providerName(provider) {
  if (provider === 'apple') return 'Apple'
  if (provider === 'google') return 'Google'
  return ''
}

/**
 * PROD-1159 — HOW LONG a WEB provider sheet may stay open before the account-deletion
 * confirmation gives up.
 *
 * ★★★ THE DEFECT. Account deletion awaited AppleID.auth.signIn() and the Google One Tap /
 *     code-client callbacks with no time limit. A popup the browser blocked, a sheet Apple's SDK
 *     never answered (measured: web Apple after a cancelled popup), or a Google exchange that went
 *     quiet left the promise pending forever, and "Confirming it's you…" spun until the app was
 *     closed (Alex, 2026-10-19). The WEB provider calls are raced against this limit and end as
 *     'cancelled': the caller's finally clears the spinner and the approved "not proven"
 *     sentence is shown.
 *
 * ★★ WEB RE-AUTH ONLY. Five minutes covers a password, a 2FA code and "trust this browser".
 *     Native re-auth has NO limit (the iOS sheets, ASAuthorizationController and GIDSignIn,
 *     always answer), and SIGN-IN has no limit anywhere: a limit there drops a sign-in that
 *     succeeds (review fb30d218 H1/H2).
 *
 * ★★ A LATE ANSWER IS IGNORED, SAFELY. Once the limit has fired, the race is settled:
 *     getFreshProviderToken has already thrown 'cancelled', the screen shows "not proven" and
 *     nothing was sent. A token the provider delivers after that resolves a promise nobody
 *     awaits any more; no deletion step proceeds on it. The patient presses again for a fresh
 *     sheet. `onLimit` lets the caller abandon what the SDK allows (L1): Google One Tap is
 *     cancelled and no code popup opens afterwards. Neither the Google code popup nor the Apple
 *     popup can be closed from here (neither SDK returns a handle or a close/cancel call), so a
 *     popup left open after the limit just does nothing when used.
 */
export const REAUTH_WEB_LIMIT_MS = 5 * 60 * 1000

/** `promise`, or (after calling `onLimit`) a rejection with 'cancelled' after `ms` without an answer. */
export function withProviderTimeout(promise, ms = REAUTH_WEB_LIMIT_MS, onLimit = null) {
  let timer
  const limit = new Promise((_, reject) => {
    timer = setTimeout(() => {
      try { onLimit?.() } catch { /* the provider UI is already gone */ }
      reject(new Error('cancelled'))
    }, ms)
  })
  return Promise.race([Promise.resolve(promise), limit]).finally(() => clearTimeout(timer))
}

/**
 * PROD-1159 — the One Tap moments that mean "no credential is coming". Only a dismissed moment
 * with reason 'cancel_called' is an end: 'credential_returned' means the callback has the
 * credential, and 'flow_restarted' (or an unknown reason) may still be followed by one, so
 * neither may end a spinner while a sign-in is in progress. Each getter is guarded: GIS under
 * FedCM stops reporting some of them.
 */
export function oneTapEnded(n) {
  const call = (f) => { try { return typeof n?.[f] === 'function' ? n[f]() : undefined } catch { return undefined } }
  if (call('isNotDisplayed')) return 'notDisplayed'
  if (call('isSkippedMoment')) return 'skipped'
  if (call('isDismissedMoment') && call('getDismissedReason') === 'cancel_called') return 'dismissed'
  return null
}

const APPLE_SDK_ID = 'apple-signin-script'
const APPLE_SDK_SRC = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'
const GOOGLE_SDK_ID = 'google-gsi-script'
const GOOGLE_SDK_SRC = 'https://accounts.google.com/gsi/client'

/**
 * PROD-1159 — load the web SDK for `provider` BEFORE the patient taps, so the tap reaches
 * AppleID.auth.signIn() with no await in front of it. A popup opened after an await has lost
 * the click's user activation and a browser may block it silently. Native and unknown
 * providers: nothing. Never throws (a failed load is retried by the tap itself).
 */
export function preloadProviderSdk(provider) {
  if (Capacitor.isNativePlatform() || typeof document === 'undefined') return
  const load = provider === 'apple' ? loadScript(APPLE_SDK_ID, APPLE_SDK_SRC)
    : provider === 'google' && GOOGLE_CLIENT_ID ? loadScript(GOOGLE_SDK_ID, GOOGLE_SDK_SRC) : null
  if (load) load.catch(() => { /* the tap loads it again */ })
}

function loadScript(id, src) {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) return resolve()
    const el = document.createElement('script')
    el.id = id
    el.src = src
    el.onload = () => resolve()
    el.onerror = () => reject(new Error('Failed to load ' + id))
    document.head.appendChild(el)
  })
}

function waitFor(getter, ms = 5000) {
  return new Promise((resolve, reject) => {
    const started = Date.now()
    const tick = setInterval(() => {
      const v = getter()
      if (v) { clearInterval(tick); resolve(v) }
      else if (Date.now() - started > ms) { clearInterval(tick); reject(new Error('timeout')) }
    }, 100)
  })
}

async function freshAppleToken() {
  if (Capacitor.isNativePlatform()) {
    await ensureSocialInit()
    const res = await SocialLogin.login({ provider: 'apple', options: { scopes: ['email', 'name'] } })
    return res?.result?.idToken || ''
  }
  // PROD-1159: with the SDK preloaded (preloadProviderSdk on the screen) there is no await
  // before signIn(), so the popup opens inside the tap's user activation.
  if (!window.AppleID?.auth) {
    await loadScript(APPLE_SDK_ID, APPLE_SDK_SRC)
    await waitFor(() => window.AppleID?.auth)
  }
  window.AppleID.auth.init({
    clientId: APPLE_WEB_CLIENT_ID,
    scope: 'name email',
    redirectURI: window.location.origin + '/signin',
    usePopup: true,
  })
  // PROD-1159: web only, REAUTH_WEB_LIMIT_MS. Apple's JS SDK has no call that closes its popup.
  const response = await withProviderTimeout(window.AppleID.auth.signIn(), REAUTH_WEB_LIMIT_MS)
  return response?.authorization?.id_token || ''
}

async function freshGoogleToken() {
  if (Capacitor.isNativePlatform()) {
    await ensureSocialInit()
    // Sign the provider session out first: a cached identity is not a re-auth.
    try { await SocialLogin.logout({ provider: 'google' }) } catch { /* not signed in */ }
    const res = await SocialLogin.login({ provider: 'google', options: { scopes: ['email', 'profile'] } })
    return res?.result?.idToken || ''
  }
  if (!GOOGLE_CLIENT_ID) throw new Error('Google Sign-In is not configured')
  if (!window.google?.accounts?.id) {
    await loadScript(GOOGLE_SDK_ID, GOOGLE_SDK_SRC)
    await waitFor(() => window.google?.accounts?.id)
  }
  // PROD-1159: the whole One Tap / code-client exchange is raced against REAUTH_WEB_LIMIT_MS.
  // At the limit One Tap is cancelled, and a moment reported after it opens no code popup.
  let over = false
  return await withProviderTimeout(new Promise((resolve, reject) => {
    try {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (r) => (r?.credential ? resolve(r.credential) : reject(new Error('cancelled'))),
        auto_select: false,
        cancel_on_tap_outside: true,
      })
      window.google.accounts.id.prompt((n) => {
        if (over) return
        const ended = oneTapEnded(n)
        // PROD-1159: One Tap cancelled (cancel_called) with no credential: an end.
        if (ended === 'dismissed') { reject(new Error('cancelled')); return }
        if (ended) {
          // One Tap unavailable — the popup returns an auth CODE, which the
          // server exchanges (_verify_google_token handles both shapes).
          const client = window.google.accounts.oauth2.initCodeClient({
            client_id: GOOGLE_CLIENT_ID,
            scope: 'openid email profile',
            ux_mode: 'popup',
            callback: (r) => (r?.code ? resolve(r.code) : reject(new Error('cancelled'))),
            error_callback: () => reject(new Error('cancelled')),
          })
          client.requestCode()
        }
      })
    } catch (e) { reject(e) }
  }), REAUTH_WEB_LIMIT_MS, () => {
    over = true
    window.google.accounts.id.cancel?.()
  })
}

/**
 * Re-authenticate with `provider` and return a fresh token for the server to
 * verify. Throws on cancellation — the caller must treat that as "not proven".
 */
export async function getFreshProviderToken(provider) {
  let token
  try {
    token = provider === 'apple' ? await freshAppleToken() : await freshGoogleToken()
  } catch (e) {
    // r_943 N2: a dismissed sheet is "not proven", never a raw SDK string on screen.
    if (isProviderCancel(e)) throw new Error('cancelled')
    throw e
  }
  if (!token) throw new Error('cancelled')
  return token
}
